
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Clock, X, Check, Calendar } from "lucide-react";
import { format } from "date-fns";
import { 
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { 
  reverseAuctionFormSchema,
  ReverseAuctionFormValues,
  reverseAuctionDefaultValues,
  auctionDurationOptions,
  timezoneOptions,
  bidderVisibilityOptions
} from "../schemas/reverseAuctionFormSchema";

interface ReverseAuctionFormProps {
  onSectionComplete: (nextSection: string) => void;
}

export const ReverseAuctionForm = ({ onSectionComplete }: ReverseAuctionFormProps) => {
  const { toast } = useToast();
  const form = useForm<ReverseAuctionFormValues>({
    resolver: zodResolver(reverseAuctionFormSchema),
    defaultValues: reverseAuctionDefaultValues,
    mode: "onChange", // Validate on change to show errors immediately
  });

  // Load saved data from localStorage on component mount
  useEffect(() => {
    const savedData = localStorage.getItem("proposal-reverse-auction");
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData) as ReverseAuctionFormValues;
        // Convert date strings back to Date objects
        if (parsedData.proposalDueDate) {
          parsedData.proposalDueDate = new Date(parsedData.proposalDueDate);
        }
        if (parsedData.auctionStartDate) {
          parsedData.auctionStartDate = new Date(parsedData.auctionStartDate);
        }
        form.reset(parsedData);
      } catch (error) {
        console.error("Error parsing saved reverse auction data:", error);
      }
    }
  }, [form]);

  // Show custom duration fields when 'custom' is selected
  const showCustomDuration = form.watch("auctionDuration") === "custom";
  const enableReverseAuction = form.watch("enableReverseAuction");
  
  // Watch for changes in dates and times to trigger validation
  const proposalDueDate = form.watch("proposalDueDate");
  const proposalDueTime = form.watch("proposalDueTime");
  const auctionStartDate = form.watch("auctionStartDate");
  const auctionStartTime = form.watch("auctionStartTime");
  
  // Trigger validation when dates or times change
  useEffect(() => {
    if (enableReverseAuction === "yes" && proposalDueDate && auctionStartDate) {
      form.trigger(["auctionStartDate"]);
    }
  }, [proposalDueDate, proposalDueTime, auctionStartDate, auctionStartTime, enableReverseAuction, form]);

  // Form submission handler
  function onSubmit(data: ReverseAuctionFormValues) {
    console.log("Reverse Auction form submitted:", data);
    // Save the form data to local storage or state management
    localStorage.setItem("proposal-reverse-auction", JSON.stringify(data));
    
    // Show success toast
    toast({
      title: "Reverse Auction settings saved",
      description: "Moving to the Review section",
    });
    
    // Navigate to the next section
    onSectionComplete("summary-review");
  }

  return (
    <div className="transition-opacity">
      <div className="flex items-center mb-4">
        <Clock className="h-6 w-6 text-black mr-2" />
        <h2 className="text-xl font-semibold text-gray-800">Reverse Auction</h2>
      </div>
      <p className="text-gray-600 mb-6">Set up a reverse auction for your RFP/Instructions</p>
      
      <Card className="mt-4">
        <CardContent className="pt-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="enableReverseAuction"
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <FormLabel>Enable Reverse Auction</FormLabel>
                    <FormDescription>
                      Allow vendors to bid against each other in real-time
                    </FormDescription>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        value={field.value}
                        variant="toggle"
                        className="flex space-x-2"
                      >
                        <div className="flex flex-col items-center">
                          <RadioGroupItem value="yes" variant="toggle" className="mb-1" />
                          <span className="text-xs">Yes</span>
                        </div>
                        <div className="flex flex-col items-center">
                          <RadioGroupItem value="no" variant="toggle" className="mb-1" />
                          <span className="text-xs">No</span>
                        </div>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              {enableReverseAuction === "yes" && (
                <>
                  {/* Timezone field - moved above proposal due date */}
                  <FormField
                    control={form.control}
                    name="timezone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Timezone</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select timezone" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {timezoneOptions.map((timezone) => (
                              <SelectItem key={timezone.value} value={timezone.value}>
                                {timezone.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          All times will be displayed in this timezone
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Date and time fields in a grid layout */}
                  <div className="space-y-4">
                    <FormLabel>Proposal Due Date & Time</FormLabel>
                    <FormDescription className="mt-0 mb-2">
                      The date and time by which proposals must be submitted
                    </FormDescription>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="proposalDueDate"
                        render={({ field }) => (
                          <FormItem>
                            <Popover>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant={"outline"}
                                    className={cn(
                                      "w-full pl-3 text-left font-normal",
                                      !field.value && "text-muted-foreground"
                                    )}
                                  >
                                    {field.value ? (
                                      format(field.value, "PPP")
                                    ) : (
                                      <span>Select date</span>
                                    )}
                                    <Calendar className="ml-auto h-4 w-4 opacity-50" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <CalendarComponent
                                  mode="single"
                                  selected={field.value}
                                  onSelect={(date) => {
                                    field.onChange(date);
                                    // Trigger validation for auction start date
                                    if (date && form.getValues("auctionStartDate")) {
                                      setTimeout(() => form.trigger("auctionStartDate"), 0);
                                    }
                                  }}
                                  disabled={(date) => {
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    return date < today;
                                  }}
                                  initialFocus
                                  className={cn("p-3 pointer-events-auto")}
                                />
                              </PopoverContent>
                            </Popover>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="proposalDueTime"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                type="time"
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e);
                                  // Trigger validation for auction start date
                                  if (form.getValues("proposalDueDate") && form.getValues("auctionStartDate")) {
                                    setTimeout(() => form.trigger("auctionStartDate"), 0);
                                  }
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <FormLabel>Auction Start Date & Time</FormLabel>
                    <FormDescription className="mt-0 mb-2">
                      When the reverse auction will begin
                    </FormDescription>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="auctionStartDate"
                        render={({ field }) => (
                          <FormItem>
                            <Popover>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant={"outline"}
                                    className={cn(
                                      "w-full pl-3 text-left font-normal",
                                      !field.value && "text-muted-foreground"
                                    )}
                                  >
                                    {field.value ? (
                                      format(field.value, "PPP")
                                    ) : (
                                      <span>Select date</span>
                                    )}
                                    <Calendar className="ml-auto h-4 w-4 opacity-50" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <CalendarComponent
                                  mode="single"
                                  selected={field.value}
                                  onSelect={(date) => {
                                    field.onChange(date);
                                    // Trigger validation immediately
                                    if (date && form.getValues("proposalDueDate")) {
                                      setTimeout(() => form.trigger("auctionStartDate"), 0);
                                    }
                                  }}
                                  disabled={(date) => {
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    return date < today;
                                  }}
                                  initialFocus
                                  className={cn("p-3 pointer-events-auto")}
                                />
                              </PopoverContent>
                            </Popover>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="auctionStartTime"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                type="time"
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e);
                                  // Trigger validation immediately
                                  if (form.getValues("proposalDueDate") && form.getValues("auctionStartDate")) {
                                    setTimeout(() => form.trigger("auctionStartDate"), 0);
                                  }
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="auctionDuration"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Auction Duration</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select duration" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {auctionDurationOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          How long the auction will run
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  {showCustomDuration && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <FormField
                        control={form.control}
                        name="customWeeks"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Weeks</FormLabel>
                            <FormControl>
                              <Input type="number" min="0" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="customDays"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Days</FormLabel>
                            <FormControl>
                              <Input type="number" min="0" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="customHours"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Hours</FormLabel>
                            <FormControl>
                              <Input type="number" min="0" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}
                  
                  <FormField
                    control={form.control}
                    name="includePricing"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Include Pricing Information</FormLabel>
                          <FormDescription>
                            Show pricing details from the pricing section to bidders
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="notifyBidders"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Notify Bidders</FormLabel>
                          <FormDescription>
                            Send email notifications when they're outbid
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="bidderVisibility"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Bidder Visibility</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select visibility option" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {bidderVisibilityOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          What information bidders can see about other bids
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="additionalNotes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Additional Notes</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Any additional instructions for bidders"
                            className="min-h-[100px] resize-none w-full"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}
              
              <div className="flex justify-end">
                <Button type="submit">Save & Continue</Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};
